import { ArrowRight, Microphone01, StopCircle } from "@untitledui/icons"
import { cx } from "@/lib/utils/cx"
import type {
  IntakeCause,
  IntakeDrivable,
} from "@/lib/api/public-consumer"
import { ChipGroup, type ChipOption } from "./chips"
import { FlowCta, FlowHeading, PhasePane, QuietLink } from "./flow-chrome"
import { useDictation } from "./voice-dictation"
import { CONSUMER_V2 } from "./copy"

// "What happened?" — chips over typing. Drivable + cause + an optional note
// with dictation (hidden entirely when SpeechRecognition isn't available on a
// secure origin). Every part is skippable.

const C = CONSUMER_V2.extras.situation

const DRIVABLE_OPTIONS: readonly ChipOption<IntakeDrivable>[] = [
  { value: "yes", label: "Yes" },
  { value: "limp", label: "Drives, but something's off" },
  { value: "no", label: "No, it can't be driven" },
]

const CAUSE_OPTIONS: readonly ChipOption<IntakeCause>[] = [
  { value: "rear_ended", label: "Rear-ended" },
  { value: "hit_something", label: "Hit something" },
  { value: "hit_parked", label: "Hit while parked" },
  { value: "side_scrape", label: "Side scrape" },
  { value: "hail", label: "Hail" },
  { value: "other", label: "Other" },
]

export function StepSituation({
  situation,
  setSituation,
  onDone,
  onSkip,
}: {
  situation: { drivable: IntakeDrivable | null; cause: IntakeCause | null; note: string }
  setSituation: (patch: Partial<{ drivable: IntakeDrivable | null; cause: IntakeCause | null; note: string }>) => void
  onDone: () => void
  onSkip: () => void
}) {
  const dictation = useDictation((text) => {
    setSituation({ note: situation.note ? `${situation.note} ${text}` : text })
  })

  return (
    <PhasePane>
      <header className="mt-5">
        <FlowHeading className="text-[28px] font-bold leading-[1.15] tracking-tight text-ink">
          {C.title}
        </FlowHeading>
        <p className="mt-2 text-[15px] leading-relaxed text-zinc-600">{C.sub}</p>
      </header>

      <div className="mt-5 flex-1 lg:pointer-fine:flex-none space-y-6">
        <ChipGroup
          label={C.drivableLabel}
          options={DRIVABLE_OPTIONS}
          value={situation.drivable}
          onChange={(drivable) => setSituation({ drivable })}
        />
        <ChipGroup
          label={C.causeLabel}
          options={CAUSE_OPTIONS}
          value={situation.cause}
          onChange={(cause) => setSituation({ cause })}
        />

        <div>
          <div className="mb-2 flex items-center justify-between">
            <label htmlFor="situation-note" className="text-[14px] font-semibold text-ink">
              {C.noteLabel}
            </label>
            {dictation.supported && (
              <button
                type="button"
                onClick={dictation.listening ? dictation.stop : dictation.start}
                className={cx(
                  "inline-flex h-11 items-center gap-1.5 rounded-full border px-4 text-[14px] font-semibold transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink",
                  dictation.listening
                    ? "border-rose-200 bg-rose-50 text-rose-700"
                    : "border-[#D5DDD8] bg-white text-[#4A5A51] hover:border-emerald-300",
                )}
              >
                {dictation.listening ? (
                  <>
                    <StopCircle className="size-4" aria-hidden />
                    {C.dictateStop}
                  </>
                ) : (
                  <>
                    <Microphone01 className="size-4" aria-hidden />
                    {C.dictate}
                  </>
                )}
              </button>
            )}
          </div>
          <textarea
            id="situation-note"
            value={situation.note}
            onChange={(e) => setSituation({ note: e.target.value })}
            placeholder={C.notePlaceholder}
            rows={3}
            maxLength={600}
            className="w-full resize-none rounded-xl border border-[#D5DDD8] bg-white px-3.5 py-3 text-[17px] text-ink placeholder:text-zinc-300 outline-none transition-all duration-150 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10"
          />
          {dictation.listening && (
            <p className="mt-1.5 text-[14px] font-medium text-rose-700" role="status">
              {C.listening}
            </p>
          )}
        </div>
      </div>

      <div className="mt-6 space-y-2">
        <FlowCta onClick={onDone}>
          {CONSUMER_V2.extras.continue}
          <ArrowRight className="size-4" aria-hidden />
        </FlowCta>
        <QuietLink onClick={onSkip}>{CONSUMER_V2.extras.skip}</QuietLink>
      </div>
    </PhasePane>
  )
}
